import { useEffect, useMemo, useState } from 'react';
import { SaveError } from './SaveError';
import { useTranslation } from 'react-i18next';
import { supabase } from '@shared/supabase/client';
import { useSupabaseQuery, useInvalidateSupabase } from '@shared/supabase/hooks';
import { formatMoney, formatPrice } from '@shared/lib/format';
import { PriceInput } from '@shared/ui';
import { useCreator } from './useCreator';

type Placement = 'reel' | 'story';
const YEAR = new Date().getFullYear();
const PLACEMENTS: Placement[] = ['reel', 'story'];

interface PriceRow { id: string; placement_type: Placement; providers_price: number | null; effective_year: number | null }

// "I work for barter" — self-declared flag so barter campaigns can match this creator.
// Lives in the rate card (it's a pricing term). Optimistic toggle; reverts on error.
function BarterRow({ providerId, value, onSaved }: { providerId: string; value: boolean; onSaved: () => void }) {
  const { t } = useTranslation('provider');
  const tr = (k: string, d: string) => t(k, { defaultValue: d });
  const [on, setOn] = useState(value);
  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState(false);
  useEffect(() => { setOn(value); }, [value]);
  const toggle = async () => {
    const next = !on;
    setOn(next); setSaving(true); setErr(false);
    const { error } = await supabase.from('instagram_providers').update({ accepts_barter: next }).eq('id', providerId);
    setSaving(false);
    if (error) { setOn(!next); setErr(true); return; }
    onSaved();
  };
  return (
    <label style={{ ...card, display: 'flex', alignItems: 'flex-start', gap: 10, marginTop: 12, cursor: saving ? 'default' : 'pointer' }}>
      <input type="checkbox" checked={on} onChange={toggle} disabled={saving} style={{ width: 18, height: 18, marginTop: 1, accentColor: 'var(--accent)', cursor: 'inherit', flexShrink: 0 }} />
      <span style={{ display: 'flex', flexDirection: 'column', gap: 2, minWidth: 0 }}>
        <span style={{ color: 'var(--text)', fontWeight: 600, fontSize: 14 }}>{tr('creator.profile.barter', 'Работаю по бартеру')}</span>
        <span style={{ color: 'var(--dim)', fontSize: 12.5, lineHeight: 1.4 }}>{tr('creator.profile.barterHint', 'Готов(а) к сотрудничеству за товар или услугу, без оплаты деньгами.')}</span>
        {err && <SaveError />}
      </span>
    </label>
  );
}

// The creator's rate card = instagram_provider_pricing (reel/story price for the current
// year). We show our fair-price benchmark as guidance so they price with the market, not blind.
export default function CreatorMenu({ barter }: { barter?: { providerId: string; value: boolean; onSaved: () => void } }) {
  const { t } = useTranslation('provider');
  const tr = (k: string, d: string, o?: Record<string, unknown>) => t(k, { defaultValue: d, ...o });
  const { provider, snapshot } = useCreator();
  const invalidate = useInvalidateSupabase();

  const pricingQ = useSupabaseQuery<PriceRow[]>(
    ['creator-pricing', provider?.id],
    async () => (provider
      ? supabase.from('instagram_provider_pricing')
          .select('id, placement_type, providers_price, effective_year')
          .eq('instagram_provider_id', provider.id).eq('effective_year', YEAR)
      : ({ data: [], error: null } as never)),
    { enabled: !!provider },
  );

  const existing = useMemo(() => {
    const m = new Map<Placement, PriceRow>();
    for (const r of pricingQ.data ?? []) if (r.placement_type === 'reel' || r.placement_type === 'story') m.set(r.placement_type, r);
    return m;
  }, [pricingQ.data]);

  const [vals, setVals] = useState<Record<Placement, string>>({ reel: '', story: '' });
  useEffect(() => {
    setVals({
      reel: existing.get('reel')?.providers_price != null ? String(existing.get('reel')!.providers_price) : '',
      story: existing.get('story')?.providers_price != null ? String(existing.get('story')!.providers_price) : '',
    });
  }, [existing]);

  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const [editing, setEditing] = useState(false);

  const save = async () => {
    if (!provider) return;
    setErr(null); setSaving(true); setSaved(false);
    for (const pl of PLACEMENTS) {
      const raw = vals[pl].trim();
      const price = raw ? Number(raw) : null;
      const row = existing.get(pl);
      if (price == null) { if (row) await supabase.from('instagram_provider_pricing').delete().eq('id', row.id); continue; }
      if (row) {
        const { error } = await supabase.from('instagram_provider_pricing').update({ providers_price: price }).eq('id', row.id);
        if (error) { setErr(error.message); setSaving(false); return; }
      } else {
        const { error } = await supabase.from('instagram_provider_pricing').insert({
          instagram_provider_id: provider.id, placement_type: pl, providers_price: price,
          currency: 'KGS', source: 'creator',
          // effective_year is a GENERATED column (from valid_from) — must NOT be inserted;
          // valid_from defaults to CURRENT_DATE. Inserting it caused "cannot insert a
          // non-DEFAULT value into column effective_year" and blocked price saving.
        });
        if (error) { setErr(error.message); setSaving(false); return; }
      }
    }
    setSaving(false); setSaved(true); setEditing(false);
    pricingQ.refetch();
    invalidate(['creator-digest']);  // Home price tile reads my_price from the digest
  };
  const cancel = () => {
    setVals({
      reel: existing.get('reel')?.providers_price != null ? String(existing.get('reel')!.providers_price) : '',
      story: existing.get('story')?.providers_price != null ? String(existing.get('story')!.providers_price) : '',
    });
    setErr(null); setEditing(false);
  };

  const benchmark = (pl: Placement): number | null =>
    pl === 'reel' ? (snapshot?.suggested_reel_price ?? snapshot?.calculated_reel_price ?? null) : null;

  return (
    <div style={wrap}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <h2 style={{ ...h2, margin: 0 }}>{tr('creator.menu.title', 'Ваши цены')}</h2>
        {!editing && !pricingQ.isLoading && (
          <button type="button" onClick={() => { setSaved(false); setEditing(true); }} style={editBtn}>{tr('creator.profile.edit', 'Изменить')}</button>
        )}
      </div>
      {editing && <p style={{ ...sub, marginTop: 6 }}>{tr('creator.menu.intro', 'Укажите, сколько вы берёте за размещение. Эти цены увидят рекламодатели в {{y}} году.', { y: YEAR })}</p>}

      {pricingQ.isLoading ? <div style={{ color: 'var(--dim)', padding: 20 }}>…</div> : (
        <div style={{ display: 'grid', gap: 12, marginTop: 12 }}>
          {PLACEMENTS.map((pl) => {
            const bm = benchmark(pl);
            const cur = existing.get(pl)?.providers_price ?? null;
            return (
              <div key={pl} style={card}>
                <div style={{ color: 'var(--dim)', fontWeight: 500, fontSize: 13, marginBottom: 6 }}>
                  {pl === 'reel' ? tr('creator.menu.reel', 'Рилс') : tr('creator.menu.story', 'Сторис')}
                </div>
                {editing ? (
                  <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                    <PriceInput value={vals[pl]}
                      onChange={(v) => { setSaved(false); setVals((s) => ({ ...s, [pl]: v })); }}
                      placeholder={tr('creator.menu.pricePh', 'Цена, сом')} style={input} />
                    <span style={{ color: 'var(--dim)', fontSize: 13 }}>{tr('creator.menu.som', 'сом')}</span>
                  </div>
                ) : (
                  <div style={{ fontSize: 19, fontWeight: 600, color: cur != null ? '#fff' : 'var(--dim)' }}>
                    {cur != null ? `${formatPrice(cur)} ${tr('creator.menu.som', 'сом')}` : tr('creator.menu.notSet', 'Не указано')}
                  </div>
                )}
                {bm != null && (
                  <div style={{ color: '#8bd450', fontSize: 12, marginTop: 8 }}>
                    {tr('creator.menu.benchmark', 'Похожие блогеры берут ~{{p}} за этот формат', { p: formatMoney(Math.round(bm)) })}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}

      {barter && <BarterRow providerId={barter.providerId} value={barter.value} onSaved={barter.onSaved} />}

      {err && <div style={{ color: '#e8607a', fontSize: 13, marginTop: 10 }}>{err}</div>}
      {editing && (
        <div style={{ display: 'flex', gap: 10, marginTop: 16 }}>
          <button type="button" onClick={cancel} style={cancelBtn}>{tr('creator.profile.cancel', 'Отмена')}</button>
          <button type="button" disabled={saving} onClick={save} style={{ ...primary, flex: 2, opacity: saving ? 0.6 : 1 }}>
            {saving ? '…' : tr('creator.menu.save', 'Сохранить цены')}
          </button>
        </div>
      )}
    </div>
  );
}

// Rendered inside the Profile page (.cr-page), which already sets width and side padding.
const wrap: React.CSSProperties = { padding: '20px 0' };
const h2: React.CSSProperties = { color: '#dde', margin: '0 0 6px', fontSize: 20 };
const sub: React.CSSProperties = { color: '#99a', fontSize: 13, margin: 0, lineHeight: 1.5 };
const card: React.CSSProperties = { background: 'rgba(255,255,255,0.03)', border: '1px solid rgba(255,255,255,0.08)', borderRadius: 12, padding: 14 };
const input: React.CSSProperties = { flex: 1, padding: '11px 12px', borderRadius: 8, background: 'rgba(0,0,0,0.25)', color: '#eee', border: '1px solid rgba(74,158,255,0.3)', fontSize: 16, fontFamily: 'inherit', width: '100%', boxSizing: 'border-box' };
const primary: React.CSSProperties = { padding: '13px 16px', borderRadius: 8, border: 'none', background: 'var(--primary)', color: '#fff', fontWeight: 600, fontSize: 15, cursor: 'pointer', fontFamily: 'inherit' };
const editBtn: React.CSSProperties = { background: 'rgba(74,158,255,0.16)', border: '1px solid rgba(74,158,255,0.45)', color: '#bcd0ff', borderRadius: 8, padding: '5px 12px', fontSize: 12.5, cursor: 'pointer', fontFamily: 'inherit' };
const cancelBtn: React.CSSProperties = { flex: 1, padding: '13px 16px', borderRadius: 8, border: '1px solid rgba(255,255,255,0.18)', background: 'transparent', color: '#cdd', fontSize: 14, cursor: 'pointer', fontFamily: 'inherit' };
